import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { printBuffer, isEmailStatus } from '@mummssoftware/utils/api/Printing';
import { APIAlert } from '../Alert';
import { EmailAcknowledgeModal } from './EmailAcknowledgeModal';
import { EmailModal } from './EmailModal';
import defaults from '../translations/en.json';
const EmailContext = /*#__PURE__*/createContext(null);
export const useEmail = () => {
  const ctx = useContext(EmailContext);

  if (!ctx) {
    throw new Error('useEmail must be used within an EmailProvider');
  }

  return ctx;
};

function formOptionToInstructions(option, senderName, subject, comments) {
  if (!option || !option.recipientemail || !option.recipientname) return null;
  return {
    recipientEmail: option.recipientemail,
    recipientName: option.recipientname,
    subject,
    comments: comments || undefined,
    senderName
  };
}

export const EmailProvider = ({
  config,
  children
}) => {
  const {
    siteEmailList,
    fetchEmailList,
    isEmailEnabled,
    getSenderName
  } = config;
  const [open, setOpen] = useState(false);
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);
  const [emailOption, setEmailOption] = useState(null);
  const [subject, setSubject] = useState('');
  const [comments, setComments] = useState('');
  const [emailAlertInfo, setEmailAlertInfo] = useState(null);
  const pendingResolveRef = useRef(null);
  const ensureEmailList = useCallback(async () => {
    if (siteEmailList && siteEmailList.length > 0) return;
    await fetchEmailList();
  }, [fetchEmailList, siteEmailList]);
  const requestEmail = useCallback(async () => {
    if (!isEmailEnabled) {
      setEmailAlertInfo({
        text: defaults.emailNotConfigured,
        severity: 'info',
        persistent: true
      });
      return null;
    }

    return new Promise(resolve => {
      pendingResolveRef.current = resolve;
      setEmailOption(null);
      setSubject('');
      setComments('');
      setAcknowledgeOpen(true);
      ensureEmailList();
    });
  }, [isEmailEnabled, ensureEmailList]);
  const handleClose = useCallback(() => {
    setOpen(false);
    setAcknowledgeOpen(false);

    if (pendingResolveRef.current) {
      pendingResolveRef.current(null);
      pendingResolveRef.current = null;
    }

    setEmailOption(null);
  }, []);
  const handleAcknowledge = useCallback(() => {
    setAcknowledgeOpen(false);
    setOpen(true);
  }, []);
  const handleSubmit = useCallback(() => {
    const senderName = getSenderName();
    const instructions = formOptionToInstructions(emailOption, senderName, subject, comments);

    if (pendingResolveRef.current) {
      pendingResolveRef.current(instructions);
      pendingResolveRef.current = null;
    }

    setOpen(false);
    setEmailOption(null);
  }, [emailOption, getSenderName, subject, comments]);
  const submitEmail = useCallback(async options => {
    var _instructions;

    let instructions = options.emailInstructions;

    if (options.wantEmail && instructions === undefined) {
      instructions = await requestEmail();
    }

    if (options.wantEmail && instructions === null) {
      return {
        handled: 'cancelled'
      };
    }

    const result = await options.getPrintPromise((_instructions = instructions) !== null && _instructions !== void 0 ? _instructions : undefined);

    if (isEmailStatus(result)) {
      const status = result;
      setEmailAlertInfo({
        text: status.message,
        severity: status.isSuccess ? 'success' : 'error'
      });
      return {
        handled: 'email'
      };
    }

    try {
      printBuffer(result);
      return {
        handled: 'email'
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Email failed. Invalid response from server.';
      setEmailAlertInfo({
        text: message,
        severity: 'error'
      });
      return {
        handled: 'email'
      };
    }
  }, [requestEmail]);
  const handleCloseAlert = useCallback(() => setEmailAlertInfo(null), []);
  const value = {
    requestEmail,
    submitEmail,
    isEmailEnabled
  };
  return /*#__PURE__*/React.createElement(EmailContext.Provider, {
    value: value
  }, children, /*#__PURE__*/React.createElement(EmailAcknowledgeModal, {
    open: acknowledgeOpen,
    onCancel: handleClose,
    onContinue: handleAcknowledge
  }), /*#__PURE__*/React.createElement(EmailModal, {
    open: open,
    onClose: handleClose,
    siteEmailList: siteEmailList,
    emailOption: emailOption,
    setEmailOption: setEmailOption,
    subject: subject,
    onSubjectChange: setSubject,
    comments: comments,
    onCommentsChange: setComments,
    onSubmit: handleSubmit
  }), emailAlertInfo && /*#__PURE__*/React.createElement(APIAlert, {
    errorInfo: emailAlertInfo,
    handleClose: handleCloseAlert,
    persistentError: !!emailAlertInfo.persistent
  }));
};